import ConditionallyWrapWithUrl from '@components/MiscComponents/ConditionallyWrapWithUrl';
import { EvIcon } from '@icon/EvIcon';
import { Bath, Bed, MLS, PropertyType, SqFoot } from '@icon/index';
import { useMeasurement } from '@util/customHooks/useMeasurement';
import { useTranslations } from 'next-intl';
import { FC } from 'react';
import {
  ListingProvider,
  ListingSnapshotItem,
  ListingSnapshotWrapper,
  ListingSnapshotpan,
} from './styles';
import { getCorrectAreaToDisplay } from '@util/formatArea';

export interface ListingSnapshotProps {
  propertySubType?: string;
  bathrooms?: number;
  bedrooms?: number;
  squareFeet?: number;
  acreage?: number;
  listOfficeName?: string;
  listOfficeUrl?: string;
  listingId?: string;
  evaId?: string;
  listAgentFullName?: string;
  listAgentURL?: string;
  brokeragePhone?: string;
  mlsComplianceObj?: {
    includeListingCourtesyOf?: boolean;
    includeListingAgent?: boolean;
    includeBrokeragePhone?: boolean;
    showLicenseNumber?: boolean;
  };
  member?: { MemberStateLicense?: string }[];
  nerenComplianceNode?: React.ReactNode;
  originatingSystemName?: string;
  isCooperativeAgency?: boolean;
  cooperativeAgencyName?: string;
}

const ListingSnapshot: FC<ListingSnapshotProps> = ({
  propertySubType,
  bathrooms,
  bedrooms,
  squareFeet,
  acreage,
  listOfficeName,
  listOfficeUrl,
  listingId,
  evaId,
  listAgentFullName,
  listAgentURL,
  brokeragePhone,
  mlsComplianceObj,
  member,
  nerenComplianceNode,
  originatingSystemName,
  isCooperativeAgency,
  cooperativeAgencyName,
}) => {
  const t = useTranslations();
  const { convertArea } = useMeasurement();

  const areaToDisplay = getCorrectAreaToDisplay(
    convertArea,
    t,
    propertySubType,
    Number(acreage),
    squareFeet,
  );

  function formatPhoneNumber(phoneNumber) {
    const cleanedNumber = phoneNumber.replace(/\D/g, '');
    if (cleanedNumber.length === 10) {
      return cleanedNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    } else {
      // Handle the case where the input is not a valid 10-digit phone number
      console.error('Invalid input: Not a 10-digit phone number');
      return phoneNumber; // or you can return another default value or handle the error as needed
    }
  }

  //boolean to check if mlsComplianceObj.includeListingCourtesyOf is set to true or not set at all
  const isListingCourtesyOfVisible =
    mlsComplianceObj?.includeListingCourtesyOf === undefined ||
    mlsComplianceObj?.includeListingCourtesyOf;

  const getListingOfficeName = () => {
    if (isCooperativeAgency && cooperativeAgencyName) {
      return cooperativeAgencyName;
    }
    return listOfficeName?.replace('V?lkers', 'Völkers') ?? '';
  };

  return (
    <ListingSnapshotWrapper $grayOut={propertySubType === 'Loading...'}>
      {mlsComplianceObj && isListingCourtesyOfVisible && listOfficeName && (
        <ListingProvider data-testid="listing-provider" $grayOut={propertySubType === 'Loading...'}>
          {t('Listing Courtesy of')} &nbsp;
          <ConditionallyWrapWithUrl href={listOfficeUrl}>
            {getListingOfficeName()}
          </ConditionallyWrapWithUrl>
          {listAgentFullName && mlsComplianceObj.includeListingAgent && (
            <>
              <span>,&nbsp;</span>
              <ConditionallyWrapWithUrl href={listAgentURL}>
                {listAgentFullName}
              </ConditionallyWrapWithUrl>
              <span>,&nbsp;</span>
            </>
          )}
          {mlsComplianceObj.includeBrokeragePhone && brokeragePhone && (
            <span>{formatPhoneNumber(brokeragePhone)}</span>
          )}
          {mlsComplianceObj.showLicenseNumber && member && (
            <span>
              , {t('State License #')}
              {member[0]?.MemberStateLicense || ''}
            </span>
          )}
          {originatingSystemName === 'NEREN' ? nerenComplianceNode : null}
        </ListingProvider>
      )}
      {propertySubType && (
        <ListingSnapshotItem>
          <PropertyType />
          <ListingSnapshotpan data-testid="property-type">{propertySubType}</ListingSnapshotpan>
        </ListingSnapshotItem>
      )}

      {bedrooms && (
        <ListingSnapshotItem>
          <Bed />
          <ListingSnapshotpan data-testid="property-bedrooms">
            {bedrooms} {t('Bd')}
          </ListingSnapshotpan>
        </ListingSnapshotItem>
      )}

      {bathrooms && (
        <ListingSnapshotItem>
          <Bath />
          <ListingSnapshotpan data-testid="property-bathrooms">
            {bathrooms} {t('Ba')}
          </ListingSnapshotpan>
        </ListingSnapshotItem>
      )}

      {areaToDisplay && (
        <ListingSnapshotItem>
          <SqFoot />
          <ListingSnapshotpan data-testid="property-area">{areaToDisplay}</ListingSnapshotpan>
        </ListingSnapshotItem>
      )}

      {evaId && (
        <ListingSnapshotItem>
          <EvIcon />
          <ListingSnapshotpan data-testid="property-evaId">{evaId}</ListingSnapshotpan>
        </ListingSnapshotItem>
      )}

      {listingId && (
        <ListingSnapshotItem>
          <MLS />
          <ListingSnapshotpan data-testid="property-mlsId">MLS#: {listingId}</ListingSnapshotpan>
        </ListingSnapshotItem>
      )}
    </ListingSnapshotWrapper>
  );
};

export default ListingSnapshot;
